import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const MENU_WIDTH = 286;

const DailySaleReportTableHead = ({
  tablehead,
  sortConfig,
  onSort,
  columnFilters,
  arrowSort = false,
}) => {
  const { t } = useTranslation();
  const base_url = process.env.REACT_APP_BASE_ASSETS_URL;
  const [columns, setColumns] = useState(tablehead);
  // Column filter menu, same markup and classes as My Inventory: { index, style } or null.
  const [openMenu, setOpenMenu] = useState(null);
  // Values ticked in the open menu (see useReportColumnFilters); applied on Apply.
  const [draftValues, setDraftValues] = useState([]);
  const [valueSearch, setValueSearch] = useState("");
  const triggerRefs = useRef({});
  const menuRef = useRef(null);

  useEffect(() => {
    setColumns(tablehead);
  }, [tablehead]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const close = () => setOpenMenu(null);
    const isInsideMenu = (target) => {
      const trigger = triggerRefs.current[openMenu.index];
      return trigger?.contains(target) || menuRef.current?.contains(target);
    };
    const handleOutsideClick = (event) => {
      if (!isInsideMenu(event.target)) close();
    };
    // The menu is fixed to the trigger's position, so close it instead of letting it drift.
    // Scrolling the value list inside the menu must not close it.
    const handleScroll = (event) => {
      if (!isInsideMenu(event.target)) close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openMenu]);

  const toggleMenu = (index, columnFilter) => {
    if (openMenu?.index === index) {
      setOpenMenu(null);
      return;
    }
    const trigger = triggerRefs.current[index];
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    let left = rect.left;
    if (left + MENU_WIDTH + 8 > viewportWidth) {
      left = Math.max(8, viewportWidth - MENU_WIDTH - 8);
    }
    if (columnFilter) {
      setDraftValues(columnFilter.selected ?? []);
      setValueSearch("");
    }
    setOpenMenu({
      index,
      style: { position: "fixed", top: rect.bottom + 6, left, width: MENU_WIDTH, zIndex: 3000 },
    });
  };

  const handleSort = (index, direction) => {
    setOpenMenu(null);
    if (onSort) onSort(index, direction);
  };

  const applyColumnFilter = (columnFilter) => {
    // Nothing ticked (like My Inventory) or every value ticked means no filter.
    const isAll = !draftValues.length || draftValues.length === columnFilter.options.length;
    columnFilter.setSelected(isAll ? null : draftValues);
    setOpenMenu(null);
  };

  if (!columns?.length) return null;

  // Original header: click the column to toggle the sort, with up/down arrows. Used outside
  // the POS reports (Quotation), which keep this instead of the filter menus.
  const renderSortArrows = (result, index) => {
    const isActive = sortConfig?.columnIndex === index;
    const arrow = result?.class === "danger-text" ? "up-down-arrow-danger" : "up-down-arrow";
    return (
      <div className="up-down-arrow-btn">
        <img
          className={
            isActive && sortConfig?.direction === "asc"
              ? "up-down-arrow-img up-down-arrow-img-forward active"
              : "up-down-arrow-img up-down-arrow-img-forward"
          }
          src={base_url + "images/icons/" + arrow + ".png"}
          alt=""
        />
        <img
          className={
            isActive && sortConfig?.direction === "desc"
              ? "up-down-arrow-img up-down-arrow-img-reverse active"
              : "up-down-arrow-img up-down-arrow-img-reverse"
          }
          src={base_url + "images/icons/" + arrow + "-reverse.png"}
          alt=""
        />
      </div>
    );
  };

  const renderValueFilter = (index, columnFilter) => {
    const search = valueSearch.trim().toLowerCase();
    const options = columnFilter.options;
    const visibleOptions = search
      ? options.filter((name) => name.toLowerCase().includes(search))
      : options;
    const draftSet = new Set(draftValues);

    const toggleValue = (name) => {
      setDraftValues((prev) =>
        prev.includes(name) ? prev.filter((value) => value !== name) : [...prev, name]
      );
    };

    return (
      <>
        <div className="com-custom-table-tag-filter-menu-search">
          <div className="com-custom-table-tag-filter-menu-search-inner d-flex align-items-center">
            <img src={base_url + "images/icons/search-icon.png"} alt="" />
            <input
              className="border-0"
              type="text"
              placeholder="Search"
              value={valueSearch}
              onChange={(e) => setValueSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="com-custom-table-tag-filter-menu-select">
          <div className="com-custom-table-tag-filter-menu-select-bar d-flex align-items-center justify-content-between">
            <div>
              <input
                className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                type="checkbox"
                id={`pos-colfilter-all-${index}`}
                onChange={(e) => setDraftValues(e.target.checked ? [...options] : [])}
                checked={options.length > 0 && draftValues.length === options.length}
              />
              <label
                className="form-check-label cursor-pointer fw-semibold"
                htmlFor={`pos-colfilter-all-${index}`}
              >
                {t("Select All")}
              </label>
            </div>
            <label>
              {draftValues.length}/{options.length} {t("Selected")}
            </label>
          </div>
          <ul className="com-custom-table-tag-filter-menu-select-list list-unstyled mt-3 ps-4">
            {visibleOptions.map((name, idx) => {
              const optId = `pos-colfilter-${index}-${idx}`;
              return (
                <li className="com-custom-table-tag-filter-menu-select-list-single" key={optId}>
                  <input
                    className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                    type="checkbox"
                    id={optId}
                    checked={draftSet.has(name)}
                    onChange={() => toggleValue(name)}
                  />
                  <label className="form-check-label cursor-pointer" htmlFor={optId}>
                    {name}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="com-custom-table-tag-filter-menu-btn-grp text-center d-flex align-items-center gap-2">
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 bg-transparent fw-semibold graythemecolor"
            onClick={() => {
              setDraftValues([]);
              setValueSearch("");
            }}
          >
            {t("Clear")}
          </button>
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 main-green-bg text-white fw-semibold"
            onClick={() => applyColumnFilter(columnFilter)}
          >
            {t("Apply")}
          </button>
        </div>
      </>
    );
  };

  const renderMenu = (index, isSortable, columnFilter) =>
    createPortal(
      <div
        className="dropdown-menu show com-custom-table-tag-filter-menu dtbs-filter-dropdown-menu dtbs-filter-portal-menu p-0 border-0 overflow-hidden"
        style={openMenu.style}
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        {isSortable && (
          <div className="asc-des-order">
            <div
              className="asc-des-order-single cursor-pointer"
              onClick={() => handleSort(index, "asc")}
            >
              <img src={base_url + "images/icons/arrow-sort-up.png"} alt="" />
              {t("Sort Ascending")}
            </div>
            <div
              className="asc-des-order-single cursor-pointer"
              onClick={() => handleSort(index, "desc")}
            >
              <img src={base_url + "images/icons/arrow-sort-down.png"} alt="" />
              {t("Sort Descending")}
            </div>
          </div>
        )}
        {columnFilter && renderValueFilter(index, columnFilter)}
      </div>,
      document.body,
    );

  return (
    <thead>
      <tr>
        {columns.map((result, index) => {
          if (result.name === "check_box") return null;

          const isSortable = result.sort === 1 && Boolean(onSort);
          const columnFilter = columnFilters?.getFilter(result.name) ?? null;
          const thClass = [result.class, index < 5 ? "daily-sale-fixed-col" : ""]
            .filter(Boolean)
            .join(" ");

          if (arrowSort) {
            return (
              <th
                key={`${result.name}-${index}`}
                data-id={index}
                className={thClass}
                onClick={() => isSortable && onSort(index)}
                style={isSortable ? { cursor: "pointer" } : undefined}
              >
                {result.image ? (
                  <img className="" src={base_url + result?.image} alt="" />
                ) : (
                  t(result.lable)
                )}
                {isSortable ? renderSortArrows(result, index) : null}
              </th>
            );
          }

          return (
            <th
              key={`${result.name}-${index}`}
              data-id={index}
              className={thClass}
            >
              {result.image ? (
                <img className="" src={base_url + result?.image} alt="" />
              ) : (
                t(result.lable)
              )}
              {isSortable || columnFilter ? (
                <div
                  className="d-inline-block com-custom-table-tag-filter dtbs-filter-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    ref={(el) => {
                      triggerRefs.current[index] = el;
                    }}
                    className="after-none bg-transparent border-0 p-0"
                    type="button"
                    aria-expanded={openMenu?.index === index}
                    onClick={() => toggleMenu(index, columnFilter)}
                  >
                    <img
                      className="ms-2 com-custom-table-tag-filter-img"
                      src={base_url + "images/icons/table-filter-icon.png"}
                      alt=""
                    />
                  </button>
                  {openMenu?.index === index
                    ? renderMenu(index, isSortable, columnFilter)
                    : null}
                </div>
              ) : null}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default DailySaleReportTableHead;
